<template>
  <div>
    <div class="form-group p-t-10">
      <div class="col-md-4 control-label">Select your business entity type *</div>
      <div class="col-md-6 col-xs-8">
        <div class="radio">
          <label>
            <input
              v-model="hasUsBusinessEntity"
              :value="1"
              type="radio"
              name="has_us_business_entity"
            />
            <i class="input-helper" />
            I have a business entity setup
          </label>
        </div>
        <div class="radio">
          <label>
            <input
              v-model="hasUsBusinessEntity"
              :value="0"
              type="radio"
              name="has_us_business_entity"
            />
            <i class="input-helper" />
            I do not have a business entity setup
          </label>
        </div>
      </div>
    </div>

    <div v-show="hasUsBusinessEntity">
      <hr />
      <div class="form-group p-t-30">
        <label class="col-md-4 control-label" for="usBusinessEntityNameInput">
          Business entity name *
        </label>
        <div class="col-md-6 col-xs-8">
          <input
            id="usBusinessEntityNameInput"
            v-model="usBusinessEntityName"
            name="us_business_entity_name"
            placeholder="Business entity name"
            type="text"
            required="required"
            class="form-control"
          />
        </div>
      </div>

      <div class="form-group">
        <label for="us_fein" class="col-md-4 control-label">
          FEIN
          <CompanyNumberTooltip market="US" />
        </label>
        <div class="col-md-6">
          <input
            id="us_fein"
            v-model="usFEIN"
            class="form-control"
            placeholder="FEIN"
            name="us_fein"
            type="text"
          />
        </div>
      </div>

      <div class="form-group">
        <label for="number_of_employees" class="col-md-4 control-label">
          Number of employees
          <span v-if="internalComplianceData.hasBeenHiredAs1099">*</span>
        </label>
        <div class="col-md-6">
          <input
            id="number_of_employees"
            v-model="numberOfEmployees"
            class="form-control"
            placeholder="Number of employees"
            name="number_of_employees"
            type="number"
            min="0"
            :required="internalComplianceData.hasBeenHiredAs1099"
          />
        </div>
      </div>

      <div class="form-group">
        <label class="col-md-4 control-label">
          Proof of registration
          <span v-if="internalComplianceData.hasBeenHiredAs1099">*&nbsp;</span>
          <WdsTooltip
            :text="
              $t(
                'You can provide a business registration certificate, a license or the state entity ID. This registration evidence will support proper classification.',
              )
            "
            left
          >
            <i class="c-indigo-500 zmdi zmdi-help"></i>
          </WdsTooltip>
        </label>
        <div class="col-md-6">
          <PickerFileFilebank
            :pre-selected-files="proofOfRegistrationFileIds"
            :use-input-field="true"
            input-name="proof_of_registration_file_ids[]"
            :default-tags="[$t('Registration proof')]"
            @files-selected="onProofOfRegistrationFilesSelected"
          />
        </div>
      </div>
    </div>

    <div class="form-group">
      <div class="col-sm-6 col-sm-offset-4 col-xs-12">
        <SubmitButton class="tw-mt-5" :text="$t('Save')" auto-submit :disabled="!canSave" />
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable import-x/extensions */
import { WdsTooltip } from '@worksome/wds-components'
import CompanyNumberTooltip from '@/components/compliances/BusinessEntity/CompanyNumberTooltip.vue'
import { booleanToIntNullable, intToBooleanNullable } from '@/utils/forms/ValueNormalisers'
import { deepClone } from '@/utils/tools'
import SubmitButton from '@/components/SubmitButton.vue'
import PickerFileFilebank from '@/components/PickerFileFilebank.vue'

export default {
  name: 'FreelancerUsBusinessEntityForm',
  components: { WdsTooltip, PickerFileFilebank, SubmitButton, CompanyNumberTooltip },
  props: {
    businessEntity: {
      type: Object,
      required: true,
    },
    complianceData: {
      type: Object,
      required: true,
    },
  },
  data() {
    const internalBusinessEntity = deepClone(this.businessEntity)
    const internalComplianceData = deepClone(this.complianceData)
    return {
      internalBusinessEntity,
      internalComplianceData,
    }
  },
  computed: {
    canSave() {
      if (this.hasUsBusinessEntity === null) {
        return false
      }
      if (this.hasUsBusinessEntity === 0) {
        return true
      }
      return this.usBusinessEntityName !== null && this.usBusinessEntityName !== ''
    },
    hasUsBusinessEntity: {
      get() {
        return booleanToIntNullable(
          this.internalComplianceData.complianceInput?.data?.has_us_business_entity ?? null,
        )
      },
      set(hasUsBusinessEntity) {
        if (this.internalComplianceData.complianceInput === null) {
          this.internalComplianceData.complianceInput = { data: {} }
        }
        this.internalComplianceData.complianceInput.data.has_us_business_entity =
          intToBooleanNullable(hasUsBusinessEntity)
        this.$emit('update:complianceData', this.internalComplianceData)
      },
    },
    numberOfEmployees: {
      get() {
        return this.internalBusinessEntity.data.number_of_employees ?? null
      },
      set(numberOfEmployees) {
        this.internalBusinessEntity.data.number_of_employees = numberOfEmployees
        this.$emit('update', this.internalBusinessEntity)
      },
    },
    proofOfRegistrationFileIds: {
      get() {
        return this.internalBusinessEntity.data.proof_of_registration_file_ids ?? []
      },
      set(proofOfRegistrationFileIds) {
        this.internalBusinessEntity.data.proof_of_registration_file_ids = proofOfRegistrationFileIds
        this.$emit('update', this.internalBusinessEntity)
      },
    },
    usBusinessEntityName: {
      get() {
        return this.internalBusinessEntity.companyName
      },
      set(usBusinessEntityName) {
        this.internalBusinessEntity.companyName = usBusinessEntityName
        this.$emit('update', this.internalBusinessEntity)
      },
    },
    usFEIN: {
      get() {
        return this.internalBusinessEntity.companyNo
      },
      set(usFEIN) {
        this.internalBusinessEntity.companyNo = usFEIN
        this.$emit('update', this.internalBusinessEntity)
      },
    },
  },
  methods: {
    onProofOfRegistrationFilesSelected(files) {
      this.proofOfRegistrationFileIds = files
    },
  },
}
</script>
